import { entityTypes } from 'constants/EntityTypes';
import partition from 'lodash/partition';

export const types = {
  REVIEW_RULES_REQUEST: 'REVIEW_RULES/REVIEW_RULES_REQUEST',
  REVIEW_RULES_SUCCESS: 'REVIEW_RULES/REVIEW_RULES_SUCCESS',
  REVIEW_RULES_FAILURE: 'REVIEW_RULES/REVIEW_RULES_FAILURE',
  CREATE_REVIEW_RULES_REQUEST: 'REVIEW_RULES/CREATE_REVIEW_RULES_REQUEST',
  CREATE_REVIEW_RULES_SUCCESS: 'REVIEW_RULES/CREATE_REVIEW_RULES_SUCCESS',
  CREATE_REVIEW_RULES_FAILURE: 'REVIEW_RULES/CREATE_REVIEW_RULES_FAILURE',
  UPDATE_REVIEW_RULES_REQUEST: 'REVIEW_RULES/UPDATE_REVIEW_RULES_REQUEST',
  UPDATE_REVIEW_RULES_SUCCESS: 'REVIEW_RULES/UPDATE_REVIEW_RULES_SUCCESS',
  UPDATE_REVIEW_RULES_FAILURE: 'REVIEW_RULES/UPDATE_REVIEW_RULES_FAILURE',
  DELETE_REVIEW_RULES_REQUEST: 'REVIEW_RULES/DELETE_REVIEW_RULES_REQUEST',
  DELETE_REVIEW_RULES_SUCCESS: 'REVIEW_RULES/DELETE_REVIEW_RULES_SUCCESS',
  DELETE_REVIEW_RULES_FAILURE: 'REVIEW_RULES/DELETE_REVIEW_RULES_FAILURE',

  REVIEW_RULES_SELECTIONS_REQUEST:
    'REVIEW_RULES/REVIEW_RULES_SELECTIONS_REQUEST',
  REVIEW_RULES_SELECTIONS_SUCCESS:
    'REVIEW_RULES/REVIEW_RULES_SELECTIONS_SUCCESS',
  REVIEW_RULES_SELECTIONS_FAILURE:
    'REVIEW_RULES/REVIEW_RULES_SELECTIONS_FAILURE',

  REORDER_REVIEW_RULES_REQUEST: 'REVIEW_RULES/REORDER_REVIEW_RULES_REQUEST',
  REORDER_REVIEW_RULES_FINISH: 'REVIEW_RULES/REORDER_REVIEW_RULES_FINISH',
};

export const initialState = {
  error: null,
  isLoading: false,
  isUpdating: false,
  isDeleting: false,
  totalUpdates: 0,
  isLoadingSelections: false,
  orgBeforeRulesByUUID: {},
  orgAfterRulesByUUID: {},
  repositoryRulesByUUID: {},
  selectionsEntityType: null,
  selectionsEntityUuid: null,
  selections: {},
};

const entityTypeToKey = {
  [entityTypes.ORGANIZATION_BEFORE_REPOSITORY]: 'orgBeforeRulesByUUID',
  [entityTypes.ORGANIZATION_AFTER_REPOSITORY]: 'orgAfterRulesByUUID',
  [entityTypes.REPOSITORY]: 'repositoryRulesByUUID',
};

export default (state = initialState, action) => {
  switch (action.type) {
    case types.REVIEW_RULES_REQUEST:
      return { ...state, isLoading: true, error: null };
    case types.REVIEW_RULES_SUCCESS: {
      const { entityUUID, entityType, rules } = action;
      if (entityType === entityTypes.ORGANIZATION_ALL) {
        const { beforeRepoRules, afterRepoRules } = partition(rules, (rule) => {
          return rule.before_repository;
        });
        return {
          ...state,
          isLoading: false,
          error: null,
          orgBeforeRulesByUUID: {
            ...state.orgBeforeRulesByUUID,
            [entityUUID]: beforeRepoRules,
          },
          orgAfterRulesByUUID: {
            ...state.orgAfterRulesByUUID,
            [entityUUID]: afterRepoRules,
          },
        };
      }
      const set = entityTypeToKey[entityType];
      return {
        ...state,
        isLoading: false,
        error: null,
        [set]: {
          ...state[set],
          [entityUUID]: rules,
        },
      };
    }
    case types.REVIEW_RULES_FAILURE:
      return { ...state, isLoading: false, error: action.error };
    case types.CREATE_REVIEW_RULES_REQUEST:
      return { ...state, isUpdating: true, error: null };
    case types.CREATE_REVIEW_RULES_SUCCESS: {
      const { entityUUID, entityType, rule } = action;
      const set = entityTypeToKey[entityType];
      return {
        ...state,
        isUpdating: false,
        error: null,
        [set]: {
          ...state[set],
          [entityUUID]: {
            ...state[set][entityUUID],
            rule,
          },
        },
      };
    }
    case types.CREATE_REVIEW_RULES_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case types.UPDATE_REVIEW_RULES_REQUEST:
      return {
        ...state,
        isUpdating: true,
        totalUpdates: state.totalUpdates + 1,
        error: null,
      };
    case types.UPDATE_REVIEW_RULES_SUCCESS:
      return { ...state, isUpdating: false };
    case types.UPDATE_REVIEW_RULES_FAILURE:
      return { ...state, isUpdating: false, error: action.error };
    case types.DELETE_REVIEW_RULES_REQUEST:
      return { ...state, isDeleting: true, error: null };
    case types.DELETE_REVIEW_RULES_SUCCESS: {
      const { entityUUID, entityType, ruleDeleting } = action;
      const set = entityTypeToKey[entityType];
      const reviewRules = state[set][entityUUID];
      const newRules = reviewRules.filter((rule) => {
        return rule.uuid !== ruleDeleting.uuid;
      });
      return {
        ...state,
        isDeleting: false,
        [set]: {
          ...state[set],
          [entityUUID]: newRules,
        },
      };
    }
    case types.DELETE_REVIEW_RULES_FAILURE:
      return { ...state, isDeleting: false, error: action.error };
    case types.REVIEW_RULES_SELECTIONS_REQUEST:
      return { ...state, isLoadingSelections: true, error: null };
    case types.REVIEW_RULES_SELECTIONS_SUCCESS: {
      const { entityType, entityUuid, selections } = action;
      return {
        ...state,
        error: null,
        isLoadingSelections: false,
        selections,
        selectionsEntityType: entityType,
        selectionsEntityUuid: entityUuid,
      };
    }
    case types.REVIEW_RULES_SELECTIONS_FAILURE:
      return { ...state, isLoadingSelections: false, error: action.error };
    case types.REORDER_REVIEW_RULES_FINISH: {
      const { entityType, entityUUID, reorderedRules } = action;
      const set = entityTypeToKey[entityType];
      return {
        ...state,
        [set]: {
          ...state[set],
          [entityUUID]: reorderedRules,
        },
      };
    }
    default:
      return state;
  }
};

export const actions = {
  fetchReviewRules: (entityUUID, entityType) => ({
    type: types.REVIEW_RULES_REQUEST,
    entityUUID,
    entityType,
  }),
  createReviewRule: (entityUUID, entityType, rule) => ({
    type: types.CREATE_REVIEW_RULES_REQUEST,
    entityUUID,
    entityType,
    rule,
  }),
  updateReviewRule: (entityUUID, entityType, rule) => ({
    type: types.UPDATE_REVIEW_RULES_REQUEST,
    entityUUID,
    entityType,
    rule,
  }),
  deleteReviewRule: (entityUUID, entityType, rule) => ({
    type: types.DELETE_REVIEW_RULES_REQUEST,
    entityUUID,
    entityType,
    rule,
  }),
  fetchReviewRulesSelections: (entityType, entityUuid) => ({
    type: types.REVIEW_RULES_SELECTIONS_REQUEST,
    entityType,
    entityUuid,
  }),
  reorderReviewRules: (
    entityType,
    entityUUID,
    reviewRules,
    sourceIndex,
    destinationIndex
  ) => ({
    type: types.REORDER_REVIEW_RULES_REQUEST,
    entityType,
    entityUUID,
    reviewRules,
    sourceIndex,
    destinationIndex,
  }),
};

export const selectors = {
  orgRulesBeforeRepoByUUID: (state, uuid) => {
    return state.reviewRules.orgBeforeRulesByUUID[uuid];
  },
  orgRulesAfterRepoByUUID: (state, uuid) => {
    return state.reviewRules.orgAfterRulesByUUID[uuid];
  },
  repositoryRulesByUUID: (state, uuid) => {
    return state.reviewRules.repositoryRulesByUUID[uuid];
  },
};
